import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import styles from "./css/ProjectPage.module.css";
import { useParams } from "react-router-dom";
import { ProjectHeader } from "@/components/headers/ProjectHeader";
import { AlternativesPanel } from "@/components/pageParts/alternative/AlternativesPanel";
import { AlternativeDashboard } from "@/components/pageParts/alternative/AlternativeDashboard";
import { useEffect, useState } from "react";
import { AlternativeData } from "@/types";
import { useGetAllAlternatives } from "@/hooks/alternative/useGetAllAlternatives";
// import { useCompareAlternatives } from "@/hooks/alternative/useCompareAlternatives";
import { LoadingPageAnimation } from "@/components/addons/LoadingPageAnimation";
import { useUpdateProject } from "@/hooks/project/useUpdateProject";
import { getEntityIdPair } from "@/utils/getEntityPairId";
import { useFetchAllProjects } from "@/utils/fetchData";
import { toatal_sqm } from "@/utils/alternative";

export const ProjectPage = () => {
    const {cityId, projectId} = useParams();
    const city = useSelector((state: RootState) => state.entity.userCity) || useSelector((state: RootState) => state.entity.cities.find(city => city.id === cityId)) || null;
    const project = useSelector((state: RootState) => state.projects.projects).find(project => project.id === projectId);
    const {userAo, entityType, userCity} = useSelector((state: RootState) => state.entity);

    const [selectedAlternative, setSelectedAlternative] = useState<AlternativeData | null>(null);
    const [favoriteId, setFavoriteId] = useState<string | null>(project?.favorite_alternative ?? null); 
    const [checkedAlternatives, setCheckedAlternatives] = useState<Set<AlternativeData>>(new Set()); 
    const [isCompare, setIsCompare] = useState(false);
    const [alternatives, setAlternatives] = useState<AlternativeData[]>([]);
    const [bestAlternative, setBestAlternative] = useState<AlternativeData | null>(null);
    const [bestAlternativeChecked, setBestAlternativeChecked] = useState<AlternativeData | null>(null);
// Hooks 
    const getAllAlternatives = useGetAllAlternatives({
        onSuccess: (data) => {
            if (data.length > 0) {
                setAlternatives(data);
                setSelectedAlternative(data[0]);
            }
        },
        onError: (error) => {
            console.error("Failed to fetch alternatives");
        }
    });

    const updateProject = useUpdateProject({
        onSuccess: (data) => {
            fetchAllProjects.mutate(getEntityIdPair(entityType, userCity, userAo));
        },
        onError: (error) => {
            console.error("Failed to update project");
        }
    })
    const fetchAllProjects = useFetchAllProjects()

    useEffect(() => {
        getAllAlternatives.mutate({
            project_id: projectId as string,
            city_id: cityId as string,
            ao_id: userAo?.id as string
        });
        fetchAllProjects.mutate(getEntityIdPair(entityType, userCity, userAo));
    }, []);


    const handleBestAlternative = (list: AlternativeData[]) => {
       let bestAlternative = list[0];
        for (const alternative of list) {
            if (alternative.consumption_per_sqm["סיום פרויקט"] / toatal_sqm(alternative) < bestAlternative.consumption_per_sqm["סיום פרויקט"] / toatal_sqm(bestAlternative)){
                bestAlternative = alternative;
            }
        }
        return bestAlternative;
    }
    
    useEffect(() => {
        const bestAlternative = handleBestAlternative(Array.from(checkedAlternatives));    
        setBestAlternativeChecked(bestAlternative);
    }, [checkedAlternatives]);
    
    useEffect(() => {
        setBestAlternative(handleBestAlternative(Array.from(alternatives)));
    }, [alternatives]);


    const handleAlternativeSelect = (alternative: AlternativeData) => {
        setSelectedAlternative(alternative);
    };
    const handleFavoriteSelect = (id: string, isFavorite: boolean) => {
        setFavoriteId(isFavorite ? id : null);
        updateProject.mutate({project_id: projectId as string, favorite_alternative: id, ...getEntityIdPair(entityType, userCity, userAo)});
    };

    const handleCheck = (alternative: AlternativeData, checked: boolean) => {
        setCheckedAlternatives(prev => {
            const newChecked = new Set(prev);
            if (checked) {
                // Only add if we haven't reached the limit of 3 for the compare
                if (newChecked.size < 3) {
                    newChecked.add(alternative);
                }
            } else {
                newChecked.delete(alternative);
                // Check if after deletion we have only 1 item left and compare is active
                if (isCompare && newChecked.size <= 1) {
                    setIsCompare(false);
                    return new Set(); 
                }
            }
            return newChecked;
        });
    };

    
    const handleCompareAlternatives = () => {
        const newIsCompare = !isCompare; 
        setIsCompare(newIsCompare);      

        if (project?.id && userAo?.id && city?.id && newIsCompare) {
            // compareAlternatives.mutate({
            //     project_id: project.id,
            //     city_id: city.id,
            //     ao_id: userAo.id,
            //     alternative_ids: Array.from(checkedAlternatives).map(alt => alt.id)
            // });
        }
        else if (!newIsCompare) {
            setCheckedAlternatives(new Set());
        }
    };

    useEffect(() => {
        if (favoriteId && alternatives.length > 0) {
            const favoriteAlternative = alternatives.find(alt => alt.id === favoriteId);
            if (favoriteAlternative) {
                setSelectedAlternative(favoriteAlternative);
            }
        }
    }, [favoriteId, alternatives]);

    useEffect(() => {
        if (alternatives.length > 0) {
            setSelectedAlternative(alternatives[alternatives.length - 1]);
        }
    }, [alternatives]);

    const dashboardAlternative = selectedAlternative || alternatives[0];
    const isLoadingProject = !project;
    const isLoadingAlternatives = getAllAlternatives.isPending;
    const hasAlternatives = alternatives.length > 0;

    if (isLoadingProject || isLoadingAlternatives) {
        return <LoadingPageAnimation />;
    }

    if (!project) {
        return (
            <div className={styles.container}>
                <h1 className="text-center text-2xl font-bold text-gray-600 mt-20">
                    הפרויקט לא נמצא
                </h1>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <ProjectHeader city={city} project={project} isCompare={isCompare}/>
            <div className={styles.content}>
                <AlternativesPanel 
                    alternatives={alternatives} 
                    userAo={userAo}
                    selectedAlternativeId={selectedAlternative?.id ?? null}
                    onAlternativeSelect={handleAlternativeSelect}
                    favoriteId={favoriteId}
                    onFavoriteSelect={handleFavoriteSelect}
                    checkedAlternatives={checkedAlternatives}
                    onCheck={handleCheck}   
                    onCompareAlternatives={handleCompareAlternatives}
                    isCompare={isCompare}
                    setIsCompare={setIsCompare}
                    bestAlternative={bestAlternative ?? alternatives[0]}
                />
                {hasAlternatives ? (
                    <AlternativeDashboard
                        checkedAlternatives={checkedAlternatives}
                        selectedAlternative={dashboardAlternative}
                        isCompare={isCompare} 
                        city={city }
                        project={project}
                        bestAlternative={bestAlternativeChecked ?? alternatives[0]}
                    />
                ) : (
                    <div className="flex-1 flex items-center justify-center">
                        <h1 className="text-center text-2xl font-bold text-gray-600">
                            אין אלטרנטיבות לפרויקט זה
                        </h1>
                    </div>
                )}
            </div>
        </div>
    );
}